// Bill-only layout. Business calculations retain the supplied BroswerComponent rules.
export function createAndroidBillReceipt({ printData, restaurant, branchAddress, gstNumber,
  fssaiNumber, receiptMessage, tableType, printSetting, logoData, width = 576, jobId,
  price, roundOffTotal, formatDate }) {
  if (!printData || !jobId) throw new Error('Bill data and a print event ID are required');
  const num = (value) => {
    const n = Number(value || 0);
    if (!Number.isFinite(n)) throw new Error('Invalid amount in bill');
    return n;
  };
  const exclusive = printSetting?.exclusiveGST ?? false;
  const rounding = printSetting?.lowerRoundOff ? 'lowerRoundOff'
    : printSetting?.upperRoundOff ? 'upperRoundOff' : 'standardRoundOff';
  const subtotal = num(printData.itemsTotal), discount = num(printData.discount);
  const charges = num(printData.otherCharges), table = num(printData.tablePrice);
  const cgst = num(printData.cgstCharges), sgst = num(printData.sgstCharges);
  const gst = exclusive ? cgst + sgst : 0;
  const unrounded = subtotal - discount + charges + table + gst;
  const total = num(roundOffTotal(unrounded, rounding));
  const round = Number((total - unrounded).toFixed(2));
  const size = Math.max(16, Math.min(34, Math.round(width / 24)));
  const lines = [];
  const text = (value, align = 'left', bold = false) => lines.push({text: String(value ?? ''), align, bold, size});
  const spacer = (height = size) => lines.push({type: 'spacer', height});
  const rule = () => lines.push({type: 'rule', dashed: true, thickness: 2, padding: 7});
  const cell = (value, weight, align = 'right', fit = true) => ({text: String(value ?? ''), weight, align, fit});
  const cells = (values, bold = false) => lines.push({type: 'cells', cells: values, bold, size});
  const amount = (label, value, bold = false) => text(`${label}  ₹ ${value.toFixed(2)}`, 'right', bold);

  if (logoData) lines.push({type: 'image', data: logoData, width: Math.round((width - 16) * 0.55), align: 'center'});
  if (restaurant) text(restaurant, 'center', true);
  if (branchAddress) text(branchAddress, 'center');
  if (fssaiNumber) text(`FSSAI Number ${fssaiNumber}`, 'center');
  spacer();
  if (printSetting?.enableCustomer && printData.customerName) {
    text(`Name : ${printData.customerName}${printData.customerMobile ? ` (${printData.customerMobile})` : ''}`);
  }
  cells([cell(printData.branchOrderNumber, 40, 'left'), cell(formatDate(printData.updatedAt), 60)]);
  const meta = [printData.orderType, printData.orderNumber, printData.tableNumber, printData.paymentType]
    .filter((v) => v !== undefined && v !== null && v !== '');
  if (meta.length) cells(meta.map((v, i) => cell(v, i === 0 ? 45 : 55 / Math.max(1, meta.length - 1), i === 0 ? 'left' : 'right')));
  rule();
  const itemRow = (name, qty, unit, value, bold = false) => cells([
    cell(name, 49, 'left', false), cell(qty, 10), cell(unit, 19), cell(value, 22)
  ], bold);
  itemRow('Item', 'Qty', 'Price', 'Amt', true);
  rule();
  (printData.orderItems ?? []).filter((item) => num(item.quantity) > 0).forEach((item) => {
    // Keep the price helper and fallback order used by the supplied web component.
    const unit = num(price(tableType?.tableType, item) || item.itemPrice || 0);
    itemRow(item.itemName, item.quantity, unit, (num(item.quantity) * unit).toFixed(2));
  });
  if (discount !== 0 || gst !== 0 || charges !== 0 || table !== 0) amount('Sub Total', subtotal);
  if (discount !== 0) amount('Discount(-)', discount);
  if (exclusive && gst !== 0) amount('GST(+)', gst);
  if (table !== 0) amount('Table Price(+)', table);
  if (charges !== 0) amount('Charges(+)', charges);
  if (round !== 0) text(`Round off  ${round > 0 ? '+' : ''}${round.toFixed(2)}`, 'right');
  amount('Grand Total', total, true);
  rule();
  if (gstNumber) {
    spacer(size * 2);
    text('Tax Summary', 'center', true);
    text(`GST ${gstNumber}`, 'center');
    spacer();
    // Preserve the uploaded component's summary values; do not change tax calculations for layout.
    const taxable = subtotal - discount;
    const rate = (value) => taxable > 0 ? ((value / taxable) * 100).toFixed(1) : '0';
    cells([cell('Taxable Amt', 42, 'left'), cell(`CGST@${rate(cgst)}%`, 29), cell(`SGST@${rate(sgst)}%`, 29)], true);
    rule();
    cells([cell(taxable.toFixed(2), 42, 'left'), cell(cgst.toFixed(2), 29), cell(sgst.toFixed(2), 29)]);
  }
  if (receiptMessage) {spacer(size * 2);text(receiptMessage, 'center', true);}
  return {jobId, printer: 'bill', format: 'lines-v2', lines};
}

// Decode in the web page, normalize to bounded monochrome PNG, then pass pixels to Android.
// Remote images must permit CORS, or be served through a same-origin endpoint.
export async function prepareBillImage(source, width = 576) {
  if (!source) return null;
  if (typeof source !== 'string') throw new Error('Bill logo must be an image URL or data URL');
  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    const timer = setTimeout(() => {img.onload = img.onerror = null; img.src = ''; reject(new Error('Bill logo load timed out'));}, 15000);
    img.crossOrigin = 'anonymous';
    img.onload = () => {clearTimeout(timer); resolve(img);};
    img.onerror = () => {clearTimeout(timer); reject(new Error('Cannot load bill logo. Check its URL and CORS permission.'));};
    img.src = source;
  });
  if (!image.naturalWidth || !image.naturalHeight) throw new Error('Bill logo is empty');
  const scale = Math.min(1, Math.round((width - 16) * 0.55) / image.naturalWidth, 512 / image.naturalHeight);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Image renderer is unavailable');
  ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  let pixels;
  try {pixels = ctx.getImageData(0, 0, canvas.width, canvas.height);}
  catch {throw new Error('Bill logo needs CORS permission or a same-origin image URL');}
  // Ordered monochrome dithering retains light logo details without relying on printer fonts.
  const matrix = [0,8,2,10,12,4,14,6,3,11,1,9,15,7,13,5];
  for (let y = 0; y < canvas.height; y++) for (let x = 0; x < canvas.width; x++) {
    const i = (y * canvas.width + x) * 4;
    const gray = (pixels.data[i] * 299 + pixels.data[i + 1] * 587 + pixels.data[i + 2] * 114) / 1000;
    const value = gray > (matrix[(y % 4) * 4 + x % 4] + 0.5) * 16 ? 255 : 0;
    pixels.data[i] = pixels.data[i + 1] = pixels.data[i + 2] = value; pixels.data[i + 3] = 255;
  }
  ctx.putImageData(pixels, 0, 0);
  const data = canvas.toDataURL('image/png');
  if (data.length > 180000) throw new Error('Bill logo is too large; use a smaller logo');
  return data;
}
